import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Building,
  ArrowRight,
  Star,
  Check,
  Users
} from 'lucide-react';

const WhyChooseUs = () => {
    const controls = useAnimation();
    const [ref, inView] = useInView({
      triggerOnce: true,
      threshold: 0.1
    });
    
    useEffect(() => {
      if (inView) {
        controls.start("visible");
      }
    }, [controls, inView]);
    
    const features = [
      {
        icon: <Star size={36} className="text-amber-500" />,
        title: "Premium Locations",
        description: "Our properties are situated in the most sought-after locations in Himachal Pradesh, offering breathtaking views and proximity to essential amenities."
      },
      {
        icon: <Building size={36} className="text-blue-600 dark:text-blue-400" />,
        title: "Architectural Excellence",
        description: "Each property is meticulously designed by renowned architects to create spaces that harmonize with the natural surroundings while offering luxurious living."
      },
      {
        icon: <Check size={36} className="text-blue-600 dark:text-blue-400" />,
        title: "Quality Assurance",
        description: "We use only premium materials and adhere to the highest construction standards, ensuring longevity, sustainability, and unmatched quality."
      },
      {
        icon: <Users size={36} className="text-amber-500" />,
        title: "Personalized Service",
        description: "Our dedicated team provides personalized attention to each client, from property selection to customization and post-purchase support."
      }
    ];
    
    return (
      <section ref={ref} className="py-24 bg-gray-50 dark:bg-gray-900 relative overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20 dark:opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-blue-300 dark:bg-blue-600 filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-64 h-64 rounded-full bg-amber-300 dark:bg-amber-700 filter blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image Side */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={controls}
              variants={{
                visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
                hidden: { opacity: 0, x: -50 }
              }}
              className="relative"
            >
              <div className="relative aspect-w-4 aspect-h-5 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1600585154526-990dced4db0d?q=80&w=2670&auto=format&fit=crop" 
                  alt="Luxury Property Interior" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent dark:from-black/60"></div>
                
                {/* Stats Card */}
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl shadow-xl p-6 w-4/5">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1">20+</div>
                      <div className="text-gray-700 dark:text-gray-300 text-sm">Years Experience</div>
                    </div>
                    <div className="text-center border-x border-gray-200 dark:border-gray-700">
                      <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1">98%</div>
                      <div className="text-gray-700 dark:text-gray-300 text-sm">Client Satisfaction</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1">43+</div>
                      <div className="text-gray-700 dark:text-gray-300 text-sm">Luxury Properties</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -top-10 -right-2 md:-right-10 bg-amber-500 dark:bg-amber-600 text-white rounded-full w-24 h-24 flex items-center justify-center text-center p-4 shadow-xl" data-aos="fade-down" data-aos-delay="400">
                <div>
                  <div className="text-2xl font-bold">A+</div>
                  <div className="text-xs">Quality Rating</div>
                </div>
              </div>
              
              {/* 3D Effect Borders */}
              <div className="absolute -bottom-5 -left-5 w-64 h-64 border-b-4 border-l-4 border-blue-500 dark:border-blue-600 rounded-bl-3xl z-0"></div>
            </motion.div>
            
            {/* Content Side */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={controls}
              variants={{
                visible: { opacity: 1, x: 0, transition: { duration: 0.8, delay: 0.2 } },
                hidden: { opacity: 0, x: 50 }
              }}
            >
              <div className="space-y-8">
                <div>
                  <div className="inline-flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded-full font-medium text-sm mb-4">
                    <Check size={16} className="mr-2" />
                    <span>Why Choose Dev Bhoomi</span>
                  </div>
                  
                  <h2 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                    Setting New Standards in <span className="text-blue-600 dark:text-blue-400">Luxury Real Estate</span>
                  </h2>
                  
                  <div className="w-20 h-1 bg-blue-600 dark:bg-blue-500 mb-6"></div>
                  
                  <p className="text-gray-700 mb-10 leading-relaxed">
                  With over 20 years of experience developing premium properties in Himachal Pradesh, Dev Bhoomi Group has built a reputation for excellence, integrity, and innovation in the luxury real estate market.
                  </p>
                </div>
                
                {/* Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {features.map((feature, idx) => (
                    <motion.div 
                      key={idx}
                      className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-700 transition-all duration-300 hover:shadow-xl"
                      whileHover={{ y: -5 }}
                      data-aos="fade-up" 
                      data-aos-delay={100 * idx}
                    >
                      <div className="w-16 h-16 rounded-full bg-gray-50 dark:bg-gray-700 flex items-center justify-center mb-4">
                        {feature.icon}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">{feature.title}</h3>
                      <p className="text-gray-600 dark:text-gray-400">{feature.description}</p>
                    </motion.div>
                  ))}
                </div>
                
                {/* CTA Button */}
                <div className="pt-8">
                  <motion.a 
                    href="#contact"
                    className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg shadow-lg transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>Discover Our Approach</span>
                    <ArrowRight size={18} className="ml-2" />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    );
  };

export default WhyChooseUs;